/** Where a quiz sits in the study. A quiz with no role has not been tagged yet. */
export const ASSESSMENT_ROLE_LABELS: Record<string, string> = {
  pre: "Pre-test",
  post: "Post-test",
  formative: "Formative (practice)",
};

export type NoticeTone = "success" | "error" | "info";

export interface Notice {
  tone: NoticeTone;
  text: string;
}

export interface LoginResponse {
  token: string;
  user: {
    assigned_id: string;
    role: "admin" | "super_admin" | "teacher" | "facilitator";
  };
}

export interface OverviewData {
  [key: string]: number;
}

export interface AdminUser {
  id?: string;
  assigned_id: string;
  /** Returned by GET /api/admin/users (COALESCE'd to ""), previously undeclared. */
  name?: string;
  role: string;
  institute?: string | null;
  center?: string | null;
  grade?: string | null;
  active: boolean;
  /** Set once the account is deleted (hidden, signed out, data kept). */
  deleted_at?: string | null;
  deleted_by?: string;
  /** Teachers only: the group they are in now. Super Admin screens only. */
  group_id?: string | null;
  group_name?: string | null;
  arm_kind?: ArmKind | null;
  group_cohort_id?: string | null;
}

export interface UsersResponse {
  users: AdminUser[];
}

export interface ImportUsersResponse {
  accepted: number;
  rejected: unknown[];
}

export interface CreatedResponse {
  id: string;
}

export interface Module {
  id: string;
  /** The PD Day this module was created inside. */
  pd_day_id: string;
  pd_day: string;
  cohort_id: string;
  title: string;
  description?: string | null;
  sequence: number;
  status: string;
  locale: string;
  cover_image_url?: string | null;
  estimated_minutes?: number | null;
  item_count: number;
  translations?: Record<string, { title?: string; description?: string }>;
}

export interface ModulesResponse {
  modules: Module[];
}

export const CONTENT_TYPES = [
  "video",
  "audio",
  "audio_slides",
  "text",
  "image",
  "gallery",
  "slides",
  "downloadable",
  "resource_pack",
  "external_link",
  "embed",
  "practical_activity",
  "reflection",
  "quiz",
  "form",
] as const;
export type ContentType = (typeof CONTENT_TYPES)[number];

export const CONTENT_TYPE_LABELS: Record<ContentType, string> = {
  video: "Video",
  audio: "Audio",
  audio_slides: "Audio with slides",
  text: "Rich-text page",
  image: "Single image",
  gallery: "Image gallery",
  slides: "Slides (PDF/PPT)",
  downloadable: "Document / download",
  resource_pack: "Folder / resource pack",
  external_link: "External link",
  embed: "Embedded content",
  practical_activity: "Practical activity",
  reflection: "Reflection question",
  quiz: "Quiz",
  form: "Feedback form",
};

export const COMPLETION_RULES = [
  "manual",
  "viewed",
  "media_threshold",
  "download_requested",
  "response_submitted",
  "quiz_attempted",
  "quiz_passed",
  "manual_approval",
  "time_spent",
] as const;
export type CompletionRule = (typeof COMPLETION_RULES)[number];

export interface ContentItem {
  id: string;
  module_id?: string;
  type: ContentType;
  title: string;
  description: string;
  position: number;
  required: boolean;
  block_next: boolean;
  order_mode: string;
  repeatable: boolean;
  estimated_minutes: number | null;
  status: string;
  locale: string;
  availability_at: string | null;
  completion_rule: CompletionRule;
  completion_threshold: number | null;
  download_allowed: boolean;
  offline_available: boolean;
}

export interface ContentItemDetail extends ContentItem {
  content: Record<string, unknown>;
  translations: Record<
    string,
    { title?: string; body?: string; media_reference?: string; strings?: Record<string, string> }
  >;
}

export interface ModuleDetail extends Module {
  content_items: ContentItem[];
}

export interface StructureItem {
  id: string;
  code?: string;
  internal_code?: string;
  name: string;
  district_id?: string;
  center_id?: string;
  cohort_id?: string;
  cohort_ids?: string[];
  /** Schools only: this school hosts PD days and owns rooms. */
  is_center?: boolean;
  /** Districts only: how many schools (not centres) are in it. */
  school_count?: number;
  start_date?: string | null;
  end_date?: string | null;
  /** Study groups only: whether the group is a treatment or the control, or not decided yet. */
  arm_kind?: ArmKind | null;
  /** Study groups only: teachers currently in the group. */
  teacher_count?: number;
}

export type ArmKind = "treatment" | "control";

export interface StudySetupGroup {
  id: string;
  name: string;
  code: string;
  arm_kind: ArmKind | null;
  teachers: number;
}

/** A cohort's arms: its groups, whether assignments are locked, and what looks wrong. */
export interface StudySetupResponse {
  cohort_id: string;
  cohort: string;
  locked_at: string | null;
  locked_by: string | null;
  groups: StudySetupGroup[];
  teachers: number;
  teachers_without_group: number;
  warnings: string[];
}

/** Before a module goes out: who would miss the items shown to chosen groups only. */
export interface AudienceRule {
  id: string;
  resource_type: "module" | "content_item";
  resource_id: string;
  target_type: string;
  target_id: string | null;
  target_value: string | null;
  include: boolean;
  label: string;
}

export interface AudienceTargets {
  groups: { id: string; name: string; cohort: string; arm_kind: string | null }[];
  districts: { id: string; name: string }[];
  institutes: { id: string; name: string; is_center: boolean }[];
  grades: string[];
  subjects: string[];
}

export interface AudienceCheck {
  group_targeted_items: { id: string; title: string }[];
  module_targeted: boolean;
  teachers: number;
  teachers_without_group: number;
  at_risk: boolean;
}

export interface StructureResponse {
  items: StructureItem[];
}

export interface ImportSchoolsResponse {
  accepted: number;
  rejected: unknown[];
}

/** A PD Day as authored: it owns modules, which own content. */
export interface PDDay {
  id: string;
  cohort_id: string;
  cohort: string;
  title: string;
  description?: string | null;
  sequence: number;
  status: string;
  module_count: number;
  item_count: number;
  /** How many times the PD Day is scheduled at a centre. */
  session_count: number;
}

export interface PDDaysResponse {
  pd_days: PDDay[];
}

export interface PDDayModule {
  id: string;
  title: string;
  description?: string | null;
  sequence: number;
  status: string;
  estimated_minutes?: number | null;
  item_count: number;
}

export interface PDDayDetail extends Omit<PDDay, "module_count" | "item_count"> {
  translations?: Record<string, { title?: string; description?: string }>;
  modules: PDDayModule[];
}

export interface ExportSummary {
  active_users: number;
  teachers: number;
  admins: number;
  content_items: number;
  modules_by_status: {
    draft: number;
    published: number;
    archived: number;
  };
  avg_completion_percent: number | null;
}

export type UploadPurpose =
  "video" | "audio" | "image" | "subtitle" | "document" | "slides" | "resource" | "thumbnail";

export interface PresignResponse {
  object_key: string;
  upload_url: string;
  public_url: string;
  expires_in_seconds: number;
}

// --- quiz questions ---------------------------------------------------------

export const QUESTION_TYPES = [
  "single_choice",
  "multiple_choice",
  "true_false",
  "short_text",
  "long_text",
  "number",
  "matching",
  "ordering",
  "fill_blank",
  "dropdown_blank",
  "image_based",
  "drag_drop",
  "scenario",
  "section",
] as const;
export type QuestionType = (typeof QUESTION_TYPES)[number];

export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  single_choice: "Single-answer multiple choice",
  multiple_choice: "Multiple-answer multiple choice",
  true_false: "True / False",
  short_text: "Short answer",
  long_text: "Long / open-text answer",
  number: "Numerical answer",
  matching: "Matching",
  ordering: "Ordering",
  fill_blank: "Fill in the blank",
  dropdown_blank: "Dropdown missing words",
  image_based: "Image-based question",
  drag_drop: "Drag and drop",
  scenario: "Scenario / case study",
  section: "Section heading",
};

export interface QuestionOption {
  id?: string;
  text: string;
  image_url?: string | null;
  is_correct: boolean;
  position: number;
  feedback?: string | null;
  /** Quiz builder only: the Urdu wording being edited; never sent with the question. */
  urdu?: string;
}

export interface MatchPair {
  id?: string;
  left_text: string;
  left_image_url?: string | null;
  right_text: string;
  position: number;
}

export interface OrderItem {
  id?: string;
  text: string;
  correct_position: number;
}

export interface BlankOption {
  id?: string;
  text: string;
  is_correct: boolean;
  position: number;
}

export interface Blank {
  id?: string;
  blank_index: number;
  correct_text: string;
  options: BlankOption[];
}

export interface Question {
  id?: string;
  quiz_id?: string;
  parent_question_id?: string | null;
  type: QuestionType;
  text: string;
  position: number;
  points: number;
  required: boolean;
  feedback_correct?: string | null;
  feedback_incorrect?: string | null;
  explanation?: string | null;
  numeric_tolerance?: number | null;
  settings: Record<string, unknown>;
  options: QuestionOption[];
  match_pairs: MatchPair[];
  order_items: OrderItem[];
  blanks: Blank[];
  children: Question[];
  /** Quiz builder only: the Urdu wording being edited; never sent with the question. */
  urdu?: string;
  explanation_urdu?: string;
}

export const FORM_FIELD_TYPES = [
  "short_text",
  "long_text",
  "single_choice",
  "checklist",
  "dropdown",
  "rating",
  "number",
  "yes_no",
  "date",
  "informational",
] as const;
export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

export const FORM_FIELD_TYPE_LABELS: Record<FormFieldType, string> = {
  short_text: "Short text",
  long_text: "Long text",
  single_choice: "Single choice",
  checklist: "Multiple choice",
  dropdown: "Dropdown",
  rating: "Rating scale",
  number: "Numerical field",
  yes_no: "Yes / No",
  date: "Date",
  informational: "Informational text",
};

export interface FieldOption {
  id?: string;
  text: string;
  position: number;
}

export interface FormField {
  id?: string;
  form_id?: string;
  type: FormFieldType;
  label: string;
  help_text?: string | null;
  position: number;
  required: boolean;
  validation: Record<string, unknown>;
  options: FieldOption[];
}

export interface PDSessionFacilitator {
  id: string;
  name: string | null;
  assigned_id: string;
}

export interface PDSession {
  id: string;
  status: "scheduled" | "in_progress" | "ended";
  start: string;
  end: string;
  cohort_id: string;
  cohort: string;
  center_id: string;
  center: string;
  pd_day_id: string;
  pd_day: string;
  module_count: number;
  room_id: string;
  room: string;
  facilitators: PDSessionFacilitator[];
  /** Schools whose teachers attend; the roster is exactly their teachers. */
  schools: { id: string; name: string }[];
  expected_teachers: number;
}

export interface PDSessionsResponse {
  pd_sessions: PDSession[];
}

/** An account named on a record, as returned by GET /api/admin/attribution. */
export interface ActorRef {
  id: string;
  name: string;
  role: string;
  /** False once the account has been deactivated; accounts are never deleted. */
  active: boolean;
}

/** Who created a record and who last changed it. Null means unknown (it predates tracking). */
export interface Attribution {
  created_by: ActorRef | null;
  created_at: string | null;
  updated_by: ActorRef | null;
  updated_at: string | null;
}

export interface AuditEntry {
  id: number;
  occurred_at: string;
  actor: ActorRef;
  /** The role held when the action happened; actor.role is the role held now. */
  actor_role: string | null;
  action: string;
  entity: string;
  entity_id: string;
  ip: string | null;
  before: unknown;
  after: unknown;
}

export interface AuditLogResponse {
  entries: AuditEntry[];
  limit: number;
  offset: number;
}
